/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as $ from "jquery";
import {UiComponent} from "./UiComponent";
import {TeamAppsUiContext} from "./TeamAppsUiContext";
import {UiPageViewBlock_Alignment, UiPageViewBlockConfig} from "../generated/UiPageViewBlockConfig";
import {UiMessagePageViewBlock_CreatorImageAlignment, UiMessagePageViewBlockConfig} from "../generated/UiMessagePageViewBlockConfig";
import {generateUUID} from "./Common";
import {UiComponentConfig} from "../generated/UiComponentConfig";
import {UiCitationPageViewBlockConfig} from "../generated/UiCitationPageViewBlockConfig";
import {UiComponentPageViewBlockConfig} from "../generated/UiComponentPageViewBlockConfig";
import {UiPageViewConfig} from "../generated/UiPageViewConfig";
import {TeamAppsUiComponentRegistry} from "./TeamAppsUiComponentRegistry";
import {createImageTemplateRenderer} from "./util/UiTextCellTemplates";

/**
 * @author Yann Massard (yamass@gmail.com)
 */

interface Row {
	$row: JQuery;
	$headerContainer: JQuery;
	$leftColumn: JQuery;
	$rightColumn: JQuery;
	blocks: Block[];
}

interface Block {
	$blockWrapper: JQuery;
	$blockContentContainer: JQuery;
	block: BlockComponent<UiPageViewBlockConfig>;
}

export class UiPageView extends UiComponent<UiPageViewConfig> {

	private $component: JQuery;
	private rows: Row[] = [];

	constructor(config: UiPageViewConfig, context: TeamAppsUiContext) {
		super(config, context);
		this.$component = $(`<div class="UiPageView"></div>`);

		if (config.blocks) {
			for (var i = 0; i < config.blocks.length; i++) {
				this.addBlock(config.blocks[i], false);
			}
		}
	}

	public getMainDomElement(): JQuery {
		return this.$component;
	}

	public addBlock(blockConfig: UiPageViewBlockConfig, before: boolean, otherBlockId?: string) {
		let row;
		if (this.rows.length == 0) {
			row = this.addRow(false);
		} else if (before && otherBlockId == null) {
			row = this.rows[0];
			if (row.blocks[0].block.getAlignment() == UiPageViewBlock_Alignment.FULL) {
				row = this.addRow(true);
			}
		} else if (!before && otherBlockId == null) {
			if (blockConfig.alignment === UiPageViewBlock_Alignment.FULL) {
				this.addRow(false);
			}
			row = this.rows[this.rows.length - 1];
		} else if (before && otherBlockId != null) {
			// TODO
		}

		let $blockWrapper = $(`<div class="panel panel-default teamapps-blurredBackgroundImage">
                    <div class="panel-body">
                    </div>
                </div>`);
		let $blockContentContainer = $blockWrapper.find('.panel-body');
		let block = new (TeamAppsUiComponentRegistry.getComponentClassForName(blockConfig._type))(blockConfig, this._context) as BlockComponent<UiPageViewBlockConfig>;
		block.getMainDomElement().appendTo($blockContentContainer);
		row.blocks.push({$blockWrapper, $blockContentContainer, block});

		// TODO prepend vs append vs insert...
		if (blockConfig.alignment === UiPageViewBlock_Alignment.FULL) {
			row.$headerContainer.append($blockWrapper);
		} else if (blockConfig.alignment === UiPageViewBlock_Alignment.LEFT) {
			row.$leftColumn.append($blockWrapper);
		} else if (blockConfig.alignment === UiPageViewBlock_Alignment.RIGHT) {
			row.$rightColumn.append($blockWrapper);
		}
		block.attachedToDom = this.attachedToDom;
	}

	private addRow(before: boolean, otherRowIndex?: number): Row {
		let $row = $('<div class="block-section row">');
		let $headerContainer = $('<div class="header-container col-md-12">').appendTo($row);
		let $leftColumn = $('<div class="left-column col-md-8">').appendTo($row);
		let $rightColumn = $('<div class="right-column col-md-4">').appendTo($row);
		let row: Row = {
			$row: $row,
			$leftColumn: $leftColumn,
			$rightColumn: $rightColumn,
			$headerContainer: $headerContainer,
			blocks: []
		};

		if (before && otherRowIndex == null) {
			this.rows.unshift(row);
			this.$component.prepend($row);
		} else if (!before && otherRowIndex == null) {
			this.rows.push(row);
			this.$component.append($row);
		} else if (before && otherRowIndex != null) {
			this.rows.splice(otherRowIndex, 0, row);
			$row.insertBefore(this.rows[otherRowIndex].$row);
		} else if (!before && otherRowIndex != null) {
			this.rows.splice(otherRowIndex + 1, 0, row);
			$row.insertAfter(this.rows[otherRowIndex].$row);
		}

		return row;
	};

	onResize(): void {
		this.rows.forEach(row => {
			row.blocks.forEach(block => block.block.reLayout());
		});
	}

	public destroy(): void {
		this.rows.forEach(row => {
			row.blocks.forEach(block => block.block.destroy());
		});
	}

	protected onAttachedToDom(): void {
		this.rows.forEach(row => row.blocks.forEach(block => block.block.attachedToDom = true));
	}
}

abstract class BlockComponent<C extends UiPageViewBlockConfig> extends UiComponent<C> {
	constructor(config: C, context: TeamAppsUiContext) {
		super(config, context);
	}

	public getAlignment() {
		return this._config.alignment;
	}
}

class UiMessagePageViewBlock extends BlockComponent<UiMessagePageViewBlockConfig> {
	private $contentWrapper: JQuery;

	constructor(config: UiMessagePageViewBlockConfig, context: TeamAppsUiContext) {
		super(config, context);

		this.$contentWrapper = $(`<div class="UiMessagePageViewBlock"></div>`);
		if (config.creatorImageUrl) {
			this.$contentWrapper.append(`<div class="creator-image-wrapper align-${UiMessagePageViewBlock_CreatorImageAlignment[config.creatorImageAlignment].toLowerCase()}">${config.creatorImageUrl ? createImageTemplateRenderer(config.creatorImageTemplate, this._context)(config.creatorImageUrl) : ''}</div>`)
		}
		if (config.headLine) {
			this.$contentWrapper.append(`<div class="headline">${config.headLine}</div>`);
		}
		if (config.text) {
			this.$contentWrapper.append(`<div class="text">${config.text}</div>`);
		}
		if (config.imageUrls && config.imageUrls.length === 1) {
			this.$contentWrapper.append(`<div class="image"><img src="${config.imageUrls[0]}"></div>`);
		} else if (config.imageUrls && config.imageUrls.length > 1) {
			let carouselId = generateUUID();
			let $carousel = $(`<div id="${carouselId}" class="carousel slide" data-ride="carousel">
                    <ol class="carousel-indicators"></ol>
                    <div class="carousel-inner" role="listbox"></div>
                    <a class="left carousel-control" href="#${carouselId}" role="button" data-slide="prev">
                        <span class="glyphicon glyphicon-chevron-left" aria-hidden="true"></span>
                    </a>
                    <a class="right carousel-control" href="#${carouselId}" role="button" data-slide="next">
                        <span class="glyphicon glyphicon-chevron-right" aria-hidden="true"></span>
                    </a>
                </div>`);

			let $indicators = $carousel.find('.carousel-indicators');
			let $images = $carousel.find('.carousel-inner');
			for (var i = 0; i < config.imageUrls.length; i++) {
				$indicators.append(`<li data-target="#${carouselId}" data-slide-to="${i}" class="${i == 0 ? 'active' : ''}"></li>`);
				$images.append(`<div class="item ${i == 0 ? 'active' : ''}"><img src="${config.imageUrls[i]}"></div>`);
			}
			$carousel.appendTo(this.$contentWrapper);
		}
	}

	public getMainDomElement(): JQuery {
		return this.$contentWrapper;
	}

	public destroy(): void {
		// nothing to do
	}
}

class UiCitationPageViewBlock extends BlockComponent<UiCitationPageViewBlockConfig> {

	private $component: JQuery;

	constructor(config: UiCitationPageViewBlockConfig, context: TeamAppsUiContext) {
		super(config, context);

		this.$component = $(`<div class="UiCitationPageViewBlock">
                <div class="creator-image-wrapper">

                </div>
                <div class="content-wrapper">

                </div>
            </div>`);
		if (config.creatorImageUrl) {
			let $imageWrapper = this.$component.find('.creator-image-wrapper');
			$imageWrapper.addClass("align-" + UiMessagePageViewBlock_CreatorImageAlignment[config.creatorImageAlignment].toLowerCase());
			$imageWrapper.append(createImageTemplateRenderer(config.creatorImageTemplate, this._context)(config.creatorImageUrl));
		}
		let $contentWrapper = this.$component.find('.content-wrapper');
		$contentWrapper.append(`<div class="citation">${config.citation}</div>`);
		$contentWrapper.append(`<div class="author">${config.author}</div>`);
	}


	public getMainDomElement(): JQuery {
		return this.$component;
	}

	public destroy(): void {
		// nothing to do
	}
}

class UiComponentPageViewBlock extends BlockComponent<UiComponentPageViewBlockConfig> {

	private $div: JQuery;
	private component: UiComponent<UiComponentConfig>;
	private $componentWrapper: JQuery;

	constructor(config: UiComponentPageViewBlockConfig, context: TeamAppsUiContext) {
		super(config, context);

		this.$div = $(`<div class="UiComponentPageViewBlock" style="height:${config.height}px">
                <div class="component-wrapper">
            </div>`);
		this.$componentWrapper = this.$div.find('.component-wrapper');

		if (config.title) {
			this.$div.prepend(`<div class="title">${config.title}</div>`);
		}

		this.component = context.createComponent(config.component);
		this.component.getMainDomElement().appendTo(this.$componentWrapper);
	}

	public getMainDomElement(): JQuery {
		return this.$div;
	}

	protected onAttachedToDom(): void {
		this.component.attachedToDom = true;
	}

	public onResize(): void {
		this.component.reLayout();
	}

	public destroy(): void {
		this.component.destroy();
	}
}

TeamAppsUiComponentRegistry.registerComponentClass("UiPageView", UiPageView);
TeamAppsUiComponentRegistry.registerComponentClass("UiMessagePageViewBlock", UiMessagePageViewBlock);
TeamAppsUiComponentRegistry.registerComponentClass("UiCitationPageViewBlock", UiCitationPageViewBlock);
TeamAppsUiComponentRegistry.registerComponentClass("UiComponentPageViewBlock", UiComponentPageViewBlock);